import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

// This component is specifically for internal staff viewing an RFQ's discussion
const InternalRfqDiscussion = ({ rfqId }) => {
    const [discussion, setDiscussion] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const fetchDiscussion = useCallback(async () => {
        if (!rfqId) return;
        setIsLoading(true);
        try {
            // Uses the internal, ID-based endpoint
            const response = await api.get(`/rfqs/${rfqId}/discussion/`);
            setDiscussion(response.data);
            setError(null);
        } catch (err) {
            if (err.response && err.response.status === 404) {
                 setError("Discussion not started. This RFQ may not be awarded yet.");
            } else {
                 setError("Could not load the discussion.");
            }
            setDiscussion(null);
        } finally {
            setIsLoading(false);
        }
    }, [rfqId]);

    useEffect(() => {
        fetchDiscussion();
    }, [fetchDiscussion]);

    useEffect(() => {
        scrollToBottom();
    }, [discussion?.messages]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        setIsSubmitting(true);
        setError(null);
        try {
            const response = await api.post(`/rfqs/${rfqId}/discussion/`, { message: newMessage });
            setDiscussion(prev => ({
                ...prev,
                messages: [...prev.messages, response.data]
            }));
            setNewMessage('');
        } catch (err) {
            setError("Failed to send message. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) return <div className="p-4 text-center text-gray-500">Loading Discussion...</div>;
    if (error && !discussion) return <div className="p-4 text-center text-red-500 italic">{error}</div>;
    if (!discussion) return null; // Don't render anything if there's no discussion to show

    return (
        <div className="bg-white shadow-md rounded-lg border border-gray-200 flex flex-col h-[70vh] mt-6">
            <header className="p-4 border-b">
                <h2 className="text-xl font-bold text-gray-800">Project Discussion with {discussion.vendor_name}</h2>
            </header>
            <main className="flex-1 p-4 overflow-y-auto bg-gray-50">
                 <div className="space-y-4">
                    {discussion.messages.map(msg => (
                        <div key={msg.id} className={`flex items-end gap-2 ${msg.sender_type === 'VENDOR' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-md p-3 rounded-lg ${msg.sender_type === 'VENDOR' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'}`}>
                                <p className="font-bold text-sm">{msg.sender_name}</p>
                                <p className="whitespace-pre-wrap">{msg.message}</p>
                                <p className="text-xs mt-1 text-right opacity-70">{new Date(msg.created_at).toLocaleString()}</p>
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
            </main>
             <footer className="p-4 border-t bg-white">
                {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
                <form onSubmit={handleSubmit} className="flex gap-2">
                    <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message to the vendor..."
                        className="flex-1 p-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        rows="2"
                        disabled={isSubmitting}
                    />
                    <button type="submit" className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:opacity-50" disabled={isSubmitting || !newMessage.trim()}>
                        {isSubmitting ? 'Sending...' : 'Send'}
                    </button>
                </form>
            </footer>
        </div>
    );
};

export default InternalRfqDiscussion;